import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { useHistory, useRouteMatch } from 'react-router-dom'
import axios from 'axios'
import {
  Box, Typography, Chip, Button, CircularProgress, Breadcrumbs, Link, Divider,
  Drawer, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import { makeStyles } from '@material-ui/core/styles'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import EditIcon from '@material-ui/icons/Edit'
import HistoryIcon from '@material-ui/icons/History'
import RestoreIcon from '@material-ui/icons/Restore'
import VisibilityIcon from '@material-ui/icons/Visibility'
import CloseIcon from '@material-ui/icons/Close'

const TRACK_LABELS = {
  subcontractor_setup: 'Subcontractor Setup',
  sub_portal: 'Subcontractor Portal',
  field_ops: 'Field Operations',
  herbicide_queue: 'Herbicide Queue',
  site_measurement: 'Site Measurement'
}

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 900,
    margin: '0 auto'
  },
  breadcrumb: {
    marginBottom: theme.spacing(2)
  },
  header: {
    marginBottom: theme.spacing(3)
  },
  trackChip: {
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    fontWeight: 500,
    marginRight: theme.spacing(1)
  },
  meta: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    marginTop: theme.spacing(1)
  },
  body: {
    marginTop: theme.spacing(3),
    lineHeight: 1.7,
    fontSize: '1rem',
    '& img': {
      maxWidth: '100%',
      borderRadius: 4,
      margin: '8px 0'
    },
    '& h1, & h2, & h3': {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(1)
    },
    '& p': {
      marginBottom: theme.spacing(1.5)
    },
    '& ul, & ol': {
      paddingLeft: theme.spacing(3)
    }
  },
  videoContainer: {
    position: 'relative',
    paddingBottom: '56.25%',
    height: 0,
    overflow: 'hidden',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: 8,
    '& iframe': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      border: 'none'
    }
  },
  coverImage: {
    width: '100%',
    maxHeight: 400,
    objectFit: 'cover',
    borderRadius: 8,
    marginBottom: theme.spacing(3)
  },
  historyDrawer: {
    width: 400,
    padding: theme.spacing(2)
  },
  revisionItem: {
    borderBottom: '1px solid #eee',
    '&:hover': {
      backgroundColor: '#f9f9f9'
    }
  },
  revisionNote: {
    fontStyle: 'italic',
    color: '#888',
    fontSize: '0.75rem'
  },
  previewBody: {
    maxHeight: 400,
    overflow: 'auto',
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    fontSize: '0.9rem',
    lineHeight: 1.6,
    '& img': { maxWidth: '100%' }
  }
}))

const getEmbedUrl = (url) => {
  if (!url) return null
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/)
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`
  const loomMatch = url.match(/loom\.com\/share\/([\w-]+)/)
  if (loomMatch) return `https://www.loom.com/embed/${loomMatch[1]}`
  return url
}

const ArticleViewer = ({ slug, ...props }) => {
  const classes = useStyles()
  const history = useHistory()
  const { url } = useRouteMatch()
  const [article, setArticle] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // History state
  const [historyOpen, setHistoryOpen] = useState(false)
  const [revisions, setRevisions] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [previewRevision, setPreviewRevision] = useState(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

  const userType = (props.user?.userType || '').toLowerCase()
  const isAdmin = userType === 'admin' || userType === 'superadmin' || (props.perms && props.perms.length > 0)
  const kbRoot = url.replace(`/article/${slug}`, '')

  useEffect(() => {
    fetchArticle()
  }, [slug]) // eslint-disable-line

  const fetchArticle = async () => {
    try {
      setLoading(true)
      const { data } = await axios.get(`/api/knowledgebase/articles/${slug}`)
      setArticle(data)
    } catch (e) {
      setError(e.response?.data?.error || 'Article not found')
    } finally {
      setLoading(false)
    }
  }

  const fetchHistory = async () => {
    if (!article) return
    try {
      setHistoryLoading(true)
      const { data } = await axios.get(`/api/knowledgebase/articles/${article._id}/history`)
      setRevisions(data)
    } catch (e) {
      console.error('Error fetching history:', e)
    } finally {
      setHistoryLoading(false)
    }
  }

  const handleOpenHistory = () => {
    setHistoryOpen(true)
    fetchHistory()
  }

  const handlePreview = async (revisionId) => {
    try {
      const { data } = await axios.get(`/api/knowledgebase/revisions/${revisionId}`)
      setPreviewRevision(data)
      setPreviewOpen(true)
    } catch (e) {
      console.error('Error fetching revision:', e)
    }
  }

  const handleRevert = async (revisionId) => {
    if (!article) return
    try {
      await axios.post(`/api/knowledgebase/articles/${article._id}/revert/${revisionId}`)
      setSnackbar({ open: true, message: 'Article reverted successfully', severity: 'success' })
      setPreviewOpen(false)
      setHistoryOpen(false)
      fetchArticle()
    } catch (e) {
      setSnackbar({ open: true, message: 'Error reverting article', severity: 'error' })
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !article) {
    return (
      <Box className={classes.root} textAlign="center" py={8}>
        <Typography variant="h6" color="error">{error || 'Article not found'}</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => history.push(kbRoot)} style={{ marginTop: 16 }}>
          Back to Knowledge Base
        </Button>
      </Box>
    )
  }

  const embedUrl = getEmbedUrl(article.videoUrl)

  return (
    <Box className={classes.root}>
      {/* Breadcrumb */}
      <Breadcrumbs className={classes.breadcrumb}>
        <Link color="inherit" href="#" onClick={(e) => { e.preventDefault(); history.push(kbRoot) }}>
          Knowledge Base
        </Link>
        <Link color="inherit" href="#" onClick={(e) => { e.preventDefault(); history.push(`${kbRoot}?track=${article.track}`) }}>
          {TRACK_LABELS[article.track] || article.track}
        </Link>
        <Typography color="textPrimary">{article.title}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box className={classes.header}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Box mb={1}>
              <Chip className={classes.trackChip} label={TRACK_LABELS[article.track]} size="small" />
              {article.status === 'draft' && (
                <Chip label="Draft" size="small" style={{ backgroundColor: '#fff3e0', color: '#e65100' }} />
              )}
            </Box>
            <Typography variant="h4" style={{ fontWeight: 600 }}>
              {article.title}
            </Typography>
            {article.summary && (
              <Typography variant="subtitle1" color="textSecondary" style={{ marginTop: 4 }}>
                {article.summary}
              </Typography>
            )}
            <Typography className={classes.meta}>
              {article.author?.displayName && `By ${article.author.displayName}`}
              {article.publishedAt && ` | Published ${new Date(article.publishedAt).toLocaleDateString()}`}
              {!article.publishedAt && article.createdAt && ` | Created ${new Date(article.createdAt).toLocaleDateString()}`}
            </Typography>
          </Box>
          {isAdmin && (
            <Box display="flex" style={{ gap: 8 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<HistoryIcon />}
                onClick={handleOpenHistory}
              >
                History
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={() => history.push(`${kbRoot}/edit/${article._id}`)}
              >
                Edit
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      <Divider />

      {/* Cover image */}
      {article.coverImage && (
        <img src={article.coverImage} alt={article.title} className={classes.coverImage} />
      )}

      {/* Video embed */}
      {embedUrl && (
        <Box className={classes.videoContainer}>
          <iframe
            src={embedUrl}
            title={article.title}
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          />
        </Box>
      )}

      {/* Article body */}
      <Box
        className={classes.body}
        dangerouslySetInnerHTML={{ __html: article.body }}
      />

      {/* Tags */}
      {article.tags && article.tags.length > 0 && (
        <Box mt={4}>
          <Divider />
          <Box mt={2}>
            {article.tags.map(tag => (
              <Chip key={tag} label={tag} size="small" variant="outlined" style={{ marginRight: 4, marginBottom: 4 }} />
            ))}
          </Box>
        </Box>
      )}

      {/* Back button */}
      <Box mt={4}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => history.push(kbRoot)}>
          Back to Knowledge Base
        </Button>
      </Box>

      {/* History Drawer */}
      <Drawer anchor="right" open={historyOpen} onClose={() => setHistoryOpen(false)}>
        <Box className={classes.historyDrawer}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Revision History</Typography>
            <IconButton size="small" onClick={() => setHistoryOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />
          {historyLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={24} />
            </Box>
          ) : revisions.length === 0 ? (
            <Box py={4} textAlign="center">
              <Typography variant="body2" color="textSecondary">No revision history yet.</Typography>
              <Typography variant="caption" color="textSecondary">History is recorded when articles are edited.</Typography>
            </Box>
          ) : (
            <List disablePadding>
              {revisions.map((rev) => (
                <ListItem key={rev._id} className={classes.revisionItem} style={{ paddingRight: 88 }}>
                  <ListItemText
                    primary={
                      <Box>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          v{rev.version} — {rev.title}
                        </Typography>
                        {rev.editNote && (
                          <Typography className={classes.revisionNote}>{rev.editNote}</Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="textSecondary">
                        {rev.editedBy?.displayName || 'Unknown'} | {new Date(rev.createdAt).toLocaleString()}
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton size="small" onClick={() => handlePreview(rev._id)} title="Preview">
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleRevert(rev._id)} title="Revert to this version">
                      <RestoreIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Drawer>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Preview — v{previewRevision?.version}: {previewRevision?.title}
        </DialogTitle>
        <DialogContent>
          {previewRevision?.summary && (
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {previewRevision.summary}
            </Typography>
          )}
          <Box
            className={classes.previewBody}
            dangerouslySetInnerHTML={{ __html: previewRevision?.body || '' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<RestoreIcon />}
            onClick={() => handleRevert(previewRevision?._id)}
          >
            Revert to This Version
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

const mapStateToProps = (state) => ({
  user: state.user,
  perms: state.perms,
  authenticated: state.authenticated
})

export default connect(mapStateToProps)(ArticleViewer)
